'use client';

import React from 'react';
import { useGlobalClientContext } from '../common/GlobalClientContext';
import ComputeItem from './ComputeItem';
import CpuItem from './CpuItem';
import ComparisonRow from './ComparisonRow';
import CpuComparisonRow from './CpuComparisonRow';
import RedirectButton from './RedirectButton';
import { convertBoldTagsToStrong } from '@altus-ui/components_v2/utils/text';

import { workloadDescTitleClassName, workloadDescClassName } from './Styles';

export default function Compute() {
  const {
    i18nHelper: { t }
  } = useGlobalClientContext();

  return (
    <>
      <div className="flex flex-col gap-3xl items-center">
        <span className={workloadDescTitleClassName}>
          {t('public.site.pricing.workload.compute.title', {})}
        </span>
        <div className="flex flex-col w-[870px] items-center">
          <span className={`${workloadDescClassName}`}>
            {t('public.site.pricing.workload.compute.desc', {})}
          </span>
        </div>
      </div>
      <RedirectButton
        className="mt-3xl !h-[48px]"
        label={t('public.site.contact.us', {})}
      />
      <div
        id="compute"
        className="flex flex-row justify-end mt-9xl w-full gap-0">
        <ComputeItem
          chip="h200-1g-pd-v1"
          title="1 GPU"
          price="$4.478"
          priceUnit={t('public.site.pricing.unit.hour', {})}
          showChipInHeader={true}
        />
        <ComputeItem
          chip="h200-2g-pd-v1"
          title="2 GPU"
          price="$8.956"
          priceUnit={t('public.site.pricing.unit.hour', {})}
          showChipInHeader={true}
        />
        <ComputeItem
          chip="h200-4g-pd-v1"
          title="4 GPU"
          price="$17.911"
          priceUnit={t('public.site.pricing.unit.hour', {})}
          showChipInHeader={true}
        />
        <ComputeItem
          chip="h200-8g-pd-v1"
          title="8 GPU"
          price="$35.822"
          priceUnit={t('public.site.pricing.unit.hour', {})}
          showChipInHeader={true}
        />
      </div>
      <div className="flex flex-col w-full gap-0 border-t border-gray-200">
        <ComparisonRow
          label="GPU profile"
          values={['h200-1g-pd-v1', 'h200-2g-pd-v1', 'h200-4g-pd-v1', 'h200-8g-pd-v1']}
        />
        <ComparisonRow
          label={t('public.site.pricing.workload.compute.vram', {})}
          values={[141, 282, 564, '1,128']}
          isWhiteBackground
        />
        <ComparisonRow
          label={t('public.site.pricing.workload.compute.vcpu', {})}
          values={[20, 41, 82, 164]}
        />
        <ComparisonRow
          label={t('public.site.pricing.workload.compute.systemRam', {})}
          values={[187, 375, 750, '1,503']}
          isWhiteBackground
        />
        <ComparisonRow
          label={t('public.site.pricing.workload.compute.localStorage', {})}
          values={[2.75, 5.5, 11, 22]}
        />
        <ComparisonRow
          label={t('public.site.pricing.workload.compute.infiniband', {})}
          values={[
            '1 x 400 Gbps',
            '2 x 400 Gbps',
            '4 x 400 Gbps',
            '8 x 400 Gbps'
          ]}
          isWhiteBackground
        />
      </div>
      <div className="flex flex-col mt-2xl w-full items-start">
        <div className="flex flex-row text-xs font-regular leading-xs text-secondary">
          {convertBoldTagsToStrong(t('public.site.pricing.gib.desc', {}))}
        </div>
        <div className="flex flex-row text-xs font-regular leading-xs text-secondary">
          {convertBoldTagsToStrong(t('public.site.pricing.gb.desc', {}))}
        </div>
        <div className="flex flex-row text-xs font-regular leading-xs text-secondary">
          {convertBoldTagsToStrong(t('public.site.pricing.tb.desc', {}))}
        </div>
      </div>

      {/* CPU Profiles Section */}
      <hr className="border-gray-200 my-9xl w-full" />
      <div className="flex flex-col gap-3xl items-center">
        <span className={workloadDescTitleClassName}>
          {t('public.site.pricing.workload.cpu.title', {})}
        </span>
        <div className="flex flex-col w-[870px] items-center">
          <span className={`${workloadDescClassName}`}>
            {t('public.site.pricing.workload.cpu.desc', {})}
          </span>
        </div>
      </div>
      <RedirectButton
        className="mt-3xl !h-[48px]"
        label={t('public.site.contact.us', {})}
      />
      <div className="flex flex-row justify-end mt-9xl w-full gap-0">
        <CpuItem
          badge={t('public.site.pricing.workload.cpu.badge.general', {})}
          badgeType="general"
          title="16 vCPUs"
          price="$1.26"
          priceUnit={t('public.site.pricing.unit.hour', {})}
        />
        <CpuItem
          badge={t('public.site.pricing.workload.cpu.badge.general', {})}
          badgeType="general"
          title="32 vCPUs"
          price="$2.52"
          priceUnit={t('public.site.pricing.unit.hour', {})}
        />
        <CpuItem
          badge={t('public.site.pricing.workload.cpu.badge.memory', {})}
          badgeType="memory"
          title="32 vCPUs"
          price="$4.38"
          priceUnit={t('public.site.pricing.unit.hour', {})}
        />
      </div>
      <div className="flex flex-col w-full gap-0 border-t border-gray-200">
        <CpuComparisonRow
          label={t('public.site.pricing.workload.cpu.profile', {})}
          values={['genoa-16c-ps-v1', 'genoa-32c-ps-v1', 'genoa-32c-pm-v1']}
        />
        <CpuComparisonRow
          label={t('public.site.pricing.workload.cpu.vcpu', {})}
          values={[16, 32, 32]}
          isWhiteBackground
        />
        <CpuComparisonRow
          label={t('public.site.pricing.workload.cpu.memory', {})}
          values={[64, 128, 512]}
        />
        <CpuComparisonRow
          label={t('public.site.pricing.workload.cpu.localStorage', {})}
          values={[1.24, 2.48, 2.48]}
          isWhiteBackground
        />
      </div>

    </>
  );
}
